import { useCallback, useEffect, useRef, useState } from 'react';

import { epsiDocsRetryCreate } from '../../openapi/operations/epsiDocsRetryCreate';
import { EPSI_RETRY_DISMISS_DELAY } from './constants';

const HTTP_ACCEPTED = 202;
const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;
const HTTP_BAD_GATEWAY = 502;

/**
 * Fáze retry jedné sady — `waiting` znamená, že se čeká na odpověď, ostatní
 * odpovídají stavům, které BE na retry vrací. `fail` je zbytek (síť, výpadek,
 * neočekávaný kód).
 */
export type EPSI_RETRY_PHASE =
  | 'waiting'
  | 'accepted'
  | 'conflict'
  | 'forbidden'
  | 'unavailable'
  | 'fail';

export type EpsiRetryState = {
  phase: EPSI_RETRY_PHASE;
  /** `detail` z odpovědi BE; u 409 nese konkrétní důvod odmítnutí. */
  detail: string | null;
};

export type UseEpsiRetry = {
  retry(id: number): Promise<void>;
  /** Stav retry po jednotlivých sadách — sada bez pokusu v mapě není. */
  retryStates: Record<number, EpsiRetryState>;
  /** Zhasnutí výsledku dřív, než vyprší `EPSI_RETRY_DISMISS_DELAY`. */
  dismissRetry(id: number): void;
};

/**
 * Spinner po dobu běhu, fajfka po přijetí. `queryState` navíc drží tlačítko
 * disabled, takže se retry nedá odpálit znovu, dokud výsledek nezhasne.
 */
export const epsiRetryQueryState = (state?: EpsiRetryState) =>
  state?.phase === 'waiting' ? 'waiting' : state?.phase === 'accepted' ? 'success' : undefined;

const phaseFromStatus = (status: number): EPSI_RETRY_PHASE => {
  switch (status) {
    case HTTP_ACCEPTED:
      return 'accepted';
    case HTTP_CONFLICT:
      return 'conflict';
    case HTTP_FORBIDDEN:
      return 'forbidden';
    case HTTP_BAD_GATEWAY:
      return 'unavailable';
    default:
      return 'fail';
  }
};

/**
 * Ruční retry sad EPSI.
 *
 * Stav se drží po jednotlivých sadách — retry se spouští z řádku přehledu
 * a spinner i výsledek patří tomu řádku. Jediný sdílený stav by při retry
 * dvou sad naráz zhasl spinner nesprávnému řádku a výsledek přiřadil cizí sadě.
 *
 * Odpověď nenese výsledek běhu — 202 znamená jen „přijato“, průběh se dál
 * sleduje přes stav sady v přehledu. 409 není chyba, jen informace, že sadu
 * retryovat nelze; důvodů má BE několik (nepodporovaný stav, otisk překonaný
 * novějším, den už není v nabídce ČÚZK) a rozlišuje je jen textem v `detail`,
 * proto se zobrazuje ten místo vlastní hlášky.
 */
export const useEpsiRetry = (onAccepted?: () => void): UseEpsiRetry => {
  const [retryStates, setRetryStates] = useState<Record<number, EpsiRetryState>>({});

  /**
   * Pořadové číslo posledního pokusu na dané sadě. Odpověď překonaného pokusu
   * se zahodí, ať nepřepíše novější výsledek ani nezhasne běžící spinner.
   */
  const attemptsRef = useRef<Record<number, number>>({});
  const dismissTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const dismissRetry = useCallback((id: number) => {
    clearTimeout(dismissTimersRef.current[id]);
    delete dismissTimersRef.current[id];

    setRetryStates((prev) => {
      if (!(id in prev)) {
        return prev;
      }

      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  // Odchod z přehledu nesmí nechat běžet naplánované dismissy.
  useEffect(
    () => () => {
      Object.values(dismissTimersRef.current).forEach(clearTimeout);
      dismissTimersRef.current = {};
    },
    [],
  );

  const retry = useCallback(
    async (id: number) => {
      const attempt = (attemptsRef.current[id] ?? 0) + 1;
      attemptsRef.current[id] = attempt;

      clearTimeout(dismissTimersRef.current[id]);
      delete dismissTimersRef.current[id];
      setRetryStates((prev) => ({ ...prev, [id]: { phase: 'waiting', detail: null } }));

      const response = await epsiDocsRetryCreate({ query: { id } });

      // Mezitím se na téže sadě spustil novější pokus — tenhle výsledek je mrtvý.
      if (attemptsRef.current[id] !== attempt) {
        return;
      }

      // Chybová odpověď nese tělo v `error`, úspěšná v `data`.
      const body = response.ok ? response.data : response.error?.data;
      const detail = typeof body?.detail === 'string' ? body.detail.trim() : '';

      setRetryStates((prev) => ({
        ...prev,
        // Prázdný `detail` musí propadnout na vlastní hlášku, ne na prázdný alert.
        [id]: { phase: phaseFromStatus(response.status), detail: detail || null },
      }));

      dismissTimersRef.current[id] = setTimeout(() => dismissRetry(id), EPSI_RETRY_DISMISS_DELAY);

      if (response.status === HTTP_ACCEPTED) {
        onAccepted?.();
      }
    },
    [dismissRetry, onAccepted],
  );

  return { retry, retryStates, dismissRetry };
};
